import { Injectable, inject } from '@angular/core';
import { SectionStateService } from '../state/section-state.service';
import { UserStateService } from '../state/user-state.service';
import { DatabaseCacheService } from '@app/data/cache/database-cache.service';
import {
  FeatureAttribute,
  isFeatureAttribute,
} from '@app/data/models/common/feature.model';

/**
 * Permission service that consolidates all permission checking logic.
 *
 * This service delegates to SectionStateService and UserStateService
 * for actual permission checks, providing a unified API for components.
 *
 * Note: Feature checks use DatabaseCacheService directly because it
 * stores the data in sessionStorage while SectionStateService signals
 * may not be synchronized.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly sectionState = inject(SectionStateService);
  private readonly userState = inject(UserStateService);
  private readonly databaseCache = inject(DatabaseCacheService);

  // Section Group Admin Role Checks

  /**
   * Check if the current user has a specific role for a section group.
   * @param sectionGroupId The section group ID to check
   * @param role The role to check for
   * @returns true if the user has the role
   */
  hasSectionGroupAdminRole(sectionGroupId: string, role: string): boolean {
    return this.userState.hasSectionGroupAdminRole(sectionGroupId, role);
  }

  /**
   * Check if the current user has section management permission for a section group.
   * @param sectionGroupId The section group ID to check
   * @returns true if the user has SECTION_MANAGEMENT role
   */
  hasSectionManagement(sectionGroupId: string): boolean {
    return this.userState.hasSectionManagement(sectionGroupId);
  }

  /**
   * Check if the current user has section group update permission.
   * @param sectionGroupId The section group ID to check
   * @returns true if the user has SECTION_GROUP_UPDATE role
   */
  hasSectionGroupUpdate(sectionGroupId: string): boolean {
    return this.userState.hasSectionGroupUpdate(sectionGroupId);
  }

  /**
   * Check if the current user has section group admin management permission.
   * @param sectionGroupId The section group ID to check
   * @returns true if the user has SECTION_GROUP_ADMIN_MANAGEMENT role
   */
  hasSectionGroupAdminManagement(sectionGroupId: string): boolean {
    return this.userState.hasSectionGroupAdminManagement(sectionGroupId);
  }

  // System Admin Role Checks

  /**
   * Check if the current user has a specific system admin role.
   * @param role The role to check for
   * @returns true if the user has the role
   */
  hasSystemAdminRole(role: string): boolean {
    return this.userState.hasSystemAdminRole(role);
  }

  /**
   * Check if the current user has all section group management permission.
   * @returns true if the user has ALL_SECTION_GROUP_MANAGEMENT role
   */
  hasAllSectionGroupManagement(): boolean {
    return this.userState.hasAllSectionGroupManagement();
  }

  /**
   * Check if the current user has parameter update permission.
   * @returns true if the user has PARAMETER_MANAGEMENT role
   */
  hasParameterUpdate(): boolean {
    return this.userState.hasParameterUpdate();
  }

  /**
   * Check if the current user has section group create permission.
   * @returns true if the user has SECTION_GROUP_CREATE role
   */
  hasSectionGroupCreate(): boolean {
    return this.userState.hasSectionGroupCreate();
  }

  /**
   * Check if the current user has system admin management permission.
   * @returns true if the user has SYSTEM_ADMIN_MANAGEMENT role
   */
  hasSystemAdminManagement(): boolean {
    return this.userState.hasSystemAdminManagement();
  }

  /**
   * Check if the current user has quota management permission.
   * @returns true if the user has QUOTA_MANAGEMENT role
   */
  hasQuotaManagement(): boolean {
    return this.userState.hasQuotaManagement();
  }

  /**
   * Check if the current user is a system admin.
   * @returns true if the user has any system admin assignment
   */
  hasSystemAdmin(): boolean {
    return this.userState.hasSystemAdmin();
  }

  // Section Access Checks

  /**
   * Check if the current user can access a specific section.
   * @param sectionId The section ID to check
   * @returns true if the user has access to the section
   */
  canAccessSection(sectionId: string): boolean {
    const sectionWithRole = this.sectionState.findSectionWithRoleById(sectionId);
    return sectionWithRole !== undefined;
  }

  /**
   * Get the roles for the currently selected section.
   * @returns Array of role strings or null if no section is selected
   */
  getSelectedSectionRoles(): string[] | null {
    return this.sectionState.selectedSectionRoles();
  }

  /**
   * Check if the contract is enabled for the currently selected section.
   * @returns true if enabled, false if disabled, null if no section selected
   */
  isContractEnabled(): boolean | null {
    return this.sectionState.selectedSectionContractEnabled();
  }

  // Section Role Checks

  /**
   * Check if the current user has a specific role in the selected section.
   * @param role The role to check for
   * @returns true if the user has the role
   */
  hasSelectedSectionRole(role: string): boolean {
    const roles = this.getSelectedSectionRoles();
    if (!roles) return false;
    return roles.some(r => r.toUpperCase() === role.toUpperCase());
  }

  /**
   * Check if the current user is primary admin for the selected section.
   * @returns true if the user has PRIMARY role
   */
  isPrimaryAdmin(): boolean {
    return this.hasSelectedSectionRole('PRIMARY');
  }

  /**
   * Check if the current user has account management permission.
   * @returns true if the user has ACCOUNT_MANAGEMENT role
   */
  hasAccountManagement(): boolean {
    return this.hasSelectedSectionRole('ACCOUNT_MANAGEMENT');
  }

  /**
   * Check if the current user has user management permission.
   * @returns true if the user has USER_MANAGEMENT role
   */
  hasUserManagement(): boolean {
    return this.hasSelectedSectionRole('USER_MANAGEMENT');
  }

  /**
   * Check if the current user has measurement permission.
   * @returns true if the user has MEASUREMENT role
   */
  hasMeasurement(): boolean {
    return this.hasSelectedSectionRole('MEASUREMENT');
  }

  /**
   * Check if the current user has refer permission.
   * @returns true if the user has REFER role
   */
  hasRefer(): boolean {
    return this.hasSelectedSectionRole('REFER');
  }

  // Feature Checks

  /**
   * Check if the specified feature is enabled for the currently selected section group.
   * Uses DatabaseCacheService directly for reliable data access.
   * @param feature The feature to check
   * @returns true if the feature is enabled
   */
  hasFeature(feature: FeatureAttribute): boolean {
    const sectionGroup = this.databaseCache.getSelectedSectionGroup();
    const functions = sectionGroup?.settings?.functionAttributes;
    if (!Array.isArray(functions)) return false;
    return functions.filter(isFeatureAttribute).includes(feature);
  }

  /**
   * Check if any of the specified features is enabled for the currently selected section group.
   * @param features The features to check
   * @returns true if any feature is enabled
   */
  hasAnyFeature(features: FeatureAttribute[]): boolean {
    return features.some((f) => this.hasFeature(f));
  }

  /**
   * Check if all of the specified features are enabled for the currently selected section group.
   * @param features The features to check
   * @returns true if all features are enabled
   */
  hasAllFeatures(features: FeatureAttribute[]): boolean {
    return features.every((f) => this.hasFeature(f));
  }

  /**
   * Check if the specified feature is enabled in any section group the user belongs to.
   * Used for cross-section features like /mentions.
   * Uses DatabaseCacheService directly for reliable data access.
   * @param feature The feature to check
   * @returns true if the feature is enabled in any section group
   */
  hasFeatureInAnySectionGroup(feature: FeatureAttribute): boolean {
    const sectionGroupDocuments = this.databaseCache.getSectionGroupDocuments();
    if (!Array.isArray(sectionGroupDocuments)) return false;
    return sectionGroupDocuments.some((group) => {
      const functions = group?.settings?.functionAttributes;
      if (!Array.isArray(functions)) return false;
      return functions.includes(feature);
    });
  }
}
